// @ts-nocheck
/**
 * Dashboard Portfolio Growth Chart Wrapper
 * Used on the Dashboard homepage; uses summary data when provided (no separate portfolio-growth API call).
 */

import React, { useState, useEffect, useMemo } from "react";
import PortfolioGrowthChart, { ViewOptions } from "./PortfolioGrowthChart";
import ChartCard from "./ChartCard";
import type {
  DashboardSummaryCardTimeframe,
  PortfolioGrowthResponse,
} from "../../../../services/tradeStatisticsApi";
import { portfolioGrowthShowsViewSelector } from "../../../../utils/portfolioGrowthUi";
import {
  endOfIanaCalendarDayUtc,
  startOfIanaCalendarDayUtc,
} from "../../../../utils/notificationPeriod";

interface DashboardPortfolioGrowthChartProps {
  brokerAccountCode: string;
  timeframe: string;
  chartLocalStart?: string | null;
  chartLocalEnd?: string | null;
  reportingTimezone?: string | null;
  onTimeframeChange?: (timeframe: string) => void;
  portfolioSize?: number;
  percentage: number;
  /** When provided (from dashboard summary), chart uses this; no portfolio-growth API call */
  portfolioGrowthData?: PortfolioGrowthResponse | null;
  portfolioGrowthLoading?: boolean;
  /** When false and summary series is empty, chart fetches `/trades/portfolio-growth` directly. */
  useExternalDataOnly?: boolean;
  title?: string;
  brokerName?: string | null;
  supportsPortfolioGrowth?: boolean;
  defaultGrowthBasis?: string | null;
}

const DashboardPortfolioGrowthChart: React.FC<DashboardPortfolioGrowthChartProps> = ({
  brokerAccountCode,
  timeframe,
  chartLocalStart,
  chartLocalEnd,
  reportingTimezone,
  onTimeframeChange,
  portfolioSize,
  percentage,
  portfolioGrowthData,
  portfolioGrowthLoading,
  useExternalDataOnly = true,
  title = "Total P&L Trend",
  brokerName,
  supportsPortfolioGrowth,
  defaultGrowthBasis,
}) => {
  const [viewOptions, setViewOptions] = useState<ViewOptions>({
    portfolioValue: true,
    tradingPnlOnly: true,
    transfers: true,
  });

  // Load view options from localStorage on mount (always enforce portfolioValue: true)
  useEffect(() => {
    const saved = localStorage.getItem('portfolioGrowthViewOptions');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object') {
          setViewOptions({ ...parsed, portfolioValue: true });
        }
      } catch (e) {
        console.warn('Failed to parse saved view options', e);
      }
    }
  }, []);

  // Save view options to localStorage when they change
  useEffect(() => {
    localStorage.setItem('portfolioGrowthViewOptions', JSON.stringify(viewOptions));
  }, [viewOptions]);

  const showViewSelector = portfolioGrowthShowsViewSelector({
    growthBasis: portfolioGrowthData?.growth_basis,
    supportsPortfolioGrowth,
    defaultGrowthBasis: defaultGrowthBasis ?? null,
  });

  const utcChartWindow = useMemo(() => {
    const zone = reportingTimezone?.trim();
    if (!chartLocalStart || !chartLocalEnd || !zone) return null;
    return {
      startDate: startOfIanaCalendarDayUtc(chartLocalStart, zone),
      endDate: endOfIanaCalendarDayUtc(chartLocalEnd, zone),
    };
  }, [chartLocalStart, chartLocalEnd, reportingTimezone]);

  const sampling = useMemo((): "hourly" | "daily" => {
    if (timeframe === "today") return "hourly";
    const start = chartLocalStart?.slice(0, 10);
    const end = chartLocalEnd?.slice(0, 10);
    if (start && end && start === end) return "hourly";
    return "daily";
  }, [chartLocalEnd, chartLocalStart, timeframe]);

  return (
    <ChartCard
      title={title}
      percentage={percentage}
      showViewSelector={showViewSelector}
      chart={
        <PortfolioGrowthChart
          brokerAccountCode={brokerAccountCode}
          timeframe={timeframe as DashboardSummaryCardTimeframe}
          chartLocalStart={chartLocalStart}
          chartLocalEnd={chartLocalEnd}
          startDate={utcChartWindow?.startDate}
          endDate={utcChartWindow?.endDate}
          reportingTimezone={reportingTimezone ?? undefined}
          sampling={sampling}
          viewOptions={viewOptions}
          onViewOptionsChange={(opts) => setViewOptions({ ...opts, portfolioValue: true })}
          useExternalDataOnly={useExternalDataOnly}
          data={portfolioGrowthData}
          dataLoading={portfolioGrowthLoading}
          brokerName={brokerName}
          supportsPortfolioGrowth={supportsPortfolioGrowth}
        />
      }
      portfolioSize={portfolioSize}
      timeframe={timeframe}
      onTimeframeChange={onTimeframeChange}
      viewOptions={viewOptions}
      onViewOptionsChange={(opts) => setViewOptions({ ...opts, portfolioValue: true })}
    />
  );
};

export default DashboardPortfolioGrowthChart;
